import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  TableRow,
  Typography,
  toCurrencyString,
  toNumberString,
  toPercent,
  useRouteDocumentTitle,
} from '@client/toolkit';
import type { CurrencyCode } from '@client/toolkit';

import { MetricCard } from '../components/metric-card';
import { DateRangeFilter } from '../components/date-range-filter';
import { dateRangeOptions } from '../utils/date-range-options';

import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import {
  PortfolioOverviewBuilding,
  PortfolioOverviewCashFlow,
  useGetReportingPortfolioOverviewQuery,
} from '@client/api';

const PLACEHOLDER = '\u2013';

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return toNumberString(value, PLACEHOLDER, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatCurrency(value: number | null | undefined, currency: string): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  return toCurrencyString(value, PLACEHOLDER, { currency: currency as CurrencyCode, maximumFractionDigits: 0 });
}

function formatPercent(value: number | null | undefined, fractionDigits = 1): string {
  if (value == null || Number.isNaN(value)) return PLACEHOLDER;
  // API returns percentage values (e.g. 75 for 75%); toPercent expects a fraction.
  return toPercent(value / 100, PLACEHOLDER, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function KpiTile({ label, value, sub, loading }: { label: string; value: string; sub?: string; loading?: boolean }) {
  return (
    <MetricCard title={label} isLoading={loading}>
      <Typography variantFontSize="h2">{value}</Typography>
      {sub ? (
        <Typography variantFontSize="sub" className="text-white/60">
          {sub}
        </Typography>
      ) : null}
    </MetricCard>
  );
}

function EnergyBadge({ label }: { label: string | null }) {
  if (!label) return <span className="text-white/40">–</span>;
  const tone =
    label === 'A' || label === 'B'
      ? 'bg-emerald-500/20 text-emerald-300'
      : label === 'C' || label === 'D'
        ? 'bg-amber-500/20 text-amber-300'
        : 'bg-rose-500/20 text-rose-300';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>{label}</span>;
}

function BuildingRegister({
  buildings,
  loading,
  currency,
  translateType,
}: {
  buildings: PortfolioOverviewBuilding[];
  loading: boolean;
  currency: string;
  translateType: (type: string | null | undefined) => string;
}) {
  const { t } = useTranslation('reporting');
  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{t('portfolioOverview.register.title')}</CardTitle>
      </CardHeader>
      <CardContent variant="fill" className="px-4 pb-4">
        <Table className="w-full" size="small">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('portfolioOverview.register.header.building')}</TableHeadCell>
              <TableHeadCell>{t('portfolioOverview.register.header.address')}</TableHeadCell>
              <TableHeadCell>{t('portfolioOverview.register.header.type')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.register.header.area')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.register.header.occupancyRate')}</TableHeadCell>
              <TableHeadCell>{t('portfolioOverview.register.header.energy')}</TableHeadCell>
              <TableHeadCell>{t('portfolioOverview.register.header.owner')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from(Array(5).keys()).map((x) => (
                  <TableRow key={x}>
                    {Array.from(Array(7).keys()).map((y) => (
                      <TableCell key={y} loading />
                    ))}
                  </TableRow>
                ))
              : buildings.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <span className="text-white/60">{t('portfolioOverview.register.empty')}</span>
                    </TableCell>
                  </TableRow>
                )
                : buildings.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell>
                        <Link
                          to={`/portfolio/buildings/${b.id}`}
                          className="flex flex-col hover:underline focus:outline-none focus:underline"
                        >
                          <span className="font-medium text-white">{b.name}</span>
                          {b.constructionYear ? (
                            <span className="text-white/60 text-xs">
                              {t('portfolioOverview.register.constructionYear', { year: b.constructionYear })}
                            </span>
                          ) : null}
                        </Link>
                      </TableCell>
                      <TableCell>{b.address || '\u2013'}</TableCell>
                      <TableCell>{translateType(b.type)}</TableCell>
                      <TableCell align="right">{formatNumber(b.totalAreaM2)}</TableCell>
                      <TableCell align="right">{formatPercent(b.occupancyRate)}</TableCell>
                      <TableCell>
                        <EnergyBadge label={b.energyLabel ?? null} />
                      </TableCell>
                      <TableCell>
                        <span className="truncate block max-w-[160px]" title={b.owners.join(', ')}>
                          {b.owners.length > 0 ? b.owners.join(', ') : '\u2013'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function BuildingCashFlowPanel({
  buildings,
  loading,
  currency,
}: {
  buildings: PortfolioOverviewBuilding[];
  loading: boolean;
  currency: string;
}) {
  const { t } = useTranslation('reporting');

  const toneForAmount = (v: number | null | undefined) =>
    v == null ? 'text-white' : v > 0 ? 'text-positive' : v < 0 ? 'text-negative' : 'text-white';

  const toneForDscr = (v: number | null | undefined) => {
    if (v == null) return 'text-white';
    if (v < 1) return 'text-negative';
    if (v < 1.5) return 'text-amber-300';
    return 'text-positive';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{t('portfolioOverview.buildingCashFlow.title')}</CardTitle>
      </CardHeader>
      <CardContent variant="fill" className="px-4 pb-4">
        <Table size="small" className="w-full">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('portfolioOverview.buildingCashFlow.header.building')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.grossIncome')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.operatingCost')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.noi')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.interestCost')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.loanRepayment')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.netCashFlow')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.buildingCashFlow.header.dscr')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(Array(5).keys()).map((x) => (
                <TableRow key={x}>
                  {Array.from(Array(8).keys()).map((y) => (
                    <TableCell key={y} loading />
                  ))}
                </TableRow>
              ))
            ) : buildings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <span className="text-white/60">{t('portfolioOverview.buildingCashFlow.empty')}</span>
                </TableCell>
              </TableRow>
            ) : (
              buildings.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>
                    <Link
                      to={`/portfolio/buildings/${b.id}`}
                      className="hover:underline focus:outline-none focus:underline"
                    >
                      <span className="font-medium text-white">{b.name}</span>
                    </Link>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(b.grossIncome, currency)}</TableCell>
                  <TableCell align="right">{formatCurrency(b.operatingCost, currency)}</TableCell>
                  <TableCell align="right">
                    <span className={toneForAmount(b.noi)}>{formatCurrency(b.noi, currency)}</span>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(b.interestCost, currency)}</TableCell>
                  <TableCell align="right">{formatCurrency(b.loanRepayment, currency)}</TableCell>
                  <TableCell align="right">
                    <span className={toneForAmount(b.netCashFlow)}>
                      {formatCurrency(b.netCashFlow, currency)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span className={toneForDscr(b.dscr)}>
                      {b.dscr != null ? formatNumber(b.dscr, 2) : PLACEHOLDER}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CashFlowPanel({
  cashFlow,
  currency,
}: {
  cashFlow: PortfolioOverviewCashFlow;
  currency: string;
}) {
  const { t } = useTranslation('reporting');
  const rows: Array<{ label: string; amount: number; perM2: number | null; emphasised?: boolean }> = [
    { label: t('portfolioOverview.cashFlow.row.grossIncome'), amount: cashFlow.grossIncome, perM2: cashFlow.grossIncomePerM2 ?? null },
    { label: t('portfolioOverview.cashFlow.row.operatingCost'), amount: -cashFlow.operatingCost, perM2: cashFlow.operatingCostPerM2 != null ? -cashFlow.operatingCostPerM2 : null },
    { label: t('portfolioOverview.cashFlow.row.noi'), amount: cashFlow.noi, perM2: cashFlow.noiPerM2 ?? null, emphasised: true },
    { label: t('portfolioOverview.cashFlow.row.interestCost'), amount: -cashFlow.interestCost, perM2: cashFlow.interestCostPerM2 != null ? -cashFlow.interestCostPerM2 : null },
    { label: t('portfolioOverview.cashFlow.row.loanRepayment'), amount: -cashFlow.loanRepayment, perM2: cashFlow.loanRepaymentPerM2 != null ? -cashFlow.loanRepaymentPerM2 : null },
    { label: t('portfolioOverview.cashFlow.row.netCashFlow'), amount: cashFlow.netCashFlow, perM2: cashFlow.netCashFlowPerM2 ?? null, emphasised: true },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle variant="highlighted">{t('portfolioOverview.cashFlow.title')}</CardTitle>
      </CardHeader>
      <CardContent variant="fill" className="px-4 pb-4">
        <Table size="small" className="w-full">
          <TableHead>
            <TableHeadRow>
              <TableHeadCell>{t('portfolioOverview.cashFlow.header.post')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.cashFlow.header.sum')}</TableHeadCell>
              <TableHeadCell align="right">{t('portfolioOverview.cashFlow.header.perM2')}</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const amountTone =
                row.amount > 0 ? 'text-positive' : row.amount < 0 ? 'text-negative' : 'text-white';
              const perM2Tone =
                row.perM2 != null && row.perM2 > 0
                  ? 'text-positive'
                  : row.perM2 != null && row.perM2 < 0
                    ? 'text-negative'
                    : 'text-white';
              return (
                <TableRow key={row.label}>
                  <TableCell>
                    <span className={row.emphasised ? 'font-semibold text-white' : 'text-white'}>{row.label}</span>
                  </TableCell>
                  <TableCell align="right">
                    <span className={`${amountTone} ${row.emphasised ? 'font-semibold' : ''}`}>
                      {formatCurrency(row.amount, currency)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <span className={perM2Tone}>{formatCurrency(row.perM2, currency)}</span>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell><span className="text-white/70">{t('portfolioOverview.cashFlow.row.dscr')}</span></TableCell>
              <TableCell align="right">{cashFlow.dscr != null ? formatNumber(cashFlow.dscr, 2) : PLACEHOLDER}</TableCell>
              <TableCell align="right">{PLACEHOLDER}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><span className="text-white/70">{t('portfolioOverview.cashFlow.row.ltv')}</span></TableCell>
              <TableCell align="right">{formatPercent(cashFlow.ltv)}</TableCell>
              <TableCell align="right">{PLACEHOLDER}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><span className="text-white/70">{t('portfolioOverview.cashFlow.row.marketValue')}</span></TableCell>
              <TableCell align="right">{formatCurrency(cashFlow.totalPropertyValue, currency)}</TableCell>
              <TableCell align="right">{formatCurrency(cashFlow.totalPropertyValuePerM2, currency)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><span className="text-white/70">{t('portfolioOverview.cashFlow.row.mortgage')}</span></TableCell>
              <TableCell align="right">{formatCurrency(cashFlow.totalMortgage, currency)}</TableCell>
              <TableCell align="right">{formatCurrency(cashFlow.totalMortgagePerM2, currency)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function ReportingPortfolioOverviewRoute() {
  const { t } = useTranslation('reporting');
  const { t: tType } = useTranslation('type');
  const [searchParams] = useSearchParams();
  useRouteDocumentTitle(t('portfolioOverview.documentTitle'), ReportingPaths.PortfolioOverview);

  const startDateParam = searchParams.get('startDate');
  const endDateParam = searchParams.get('endDate');
  const initialStartDate = startDateParam ? dayjs(startDateParam).toDate() : new Date(new Date().getFullYear(), 0, 1);
  const initialEndDate = endDateParam ? dayjs(endDateParam).toDate() : new Date(new Date().getFullYear(), 11, 31);

  const [timeRange, setTimeRange] = useState<{ startDate: Date; endDate: Date }>({
    startDate: initialStartDate,
    endDate: initialEndDate,
  });

  const { data, isLoading } = useGetReportingPortfolioOverviewQuery({
    startDate: dayjs(timeRange.startDate).format('YYYY-MM-DD'),
    endDate: dayjs(timeRange.endDate).format('YYYY-MM-DD'),
  });

  const translateType = (type: string | null | undefined): string => {
    if (!type) return '–';
    const translated = tType(`BuildingType.${type}`, { defaultValue: type });
    return translated;
  };

  const currency = data?.currency ?? 'NOK';
  const summary = data?.summary;
  const totalArea = summary?.totalAreaM2 ?? 0;

  return (
    <ReportPageContent
      breadcrumb={t('portfolioOverview.breadcrumb')}
      menu={
        <DateRangeFilter
          options={[
            dateRangeOptions().thisYear,
            dateRangeOptions().previousYear,
            dateRangeOptions().custom,
          ]}
          pickerMode="year"
          onChange={(d) => setTimeRange({ startDate: d[0], endDate: d[1] })}
          initialStartDate={timeRange.startDate}
          initialEndDate={timeRange.endDate}
        />
      }
    >
      <div className="flex gap-5 justify-between h-[146px] text-white">
        <KpiTile
          label={t('portfolioOverview.kpi.buildings')}
          value={formatNumber(summary?.buildingCount ?? 0)}
          loading={isLoading}
        />
        <KpiTile
          label={t('portfolioOverview.kpi.totalArea')}
          value={`${formatNumber(totalArea)} m²`}
          loading={isLoading}
        />
        <KpiTile
          label={t('portfolioOverview.kpi.occupancyRate')}
          value={formatPercent(summary?.occupancyRate)}
          loading={isLoading}
        />
        <KpiTile
          label={t('portfolioOverview.kpi.vacancyRate')}
          value={formatPercent(summary?.vacancyRate)}
          loading={isLoading}
        />
      </div>

      <BuildingRegister
        buildings={data?.buildings ?? []}
        loading={isLoading}
        currency={currency}
        translateType={translateType}
      />

      <BuildingCashFlowPanel
        buildings={data?.buildings ?? []}
        loading={isLoading}
        currency={currency}
      />

      {data ? <CashFlowPanel cashFlow={data.portfolioCashFlow} currency={currency} /> : null}
    </ReportPageContent>
  );
}
